/*
 * Drives every <ambient-video> on the page: a muted, looping video that fades in
 * over a poster while it is on screen and pauses when it is not.
 *
 * Two activation rules, chosen per element. By default only the element the
 * vertical centre of the viewport falls inside plays. The stripes of the
 * portfolio listing sit flush against each other, so `top <= centre && bottom >
 * centre` always selects exactly one of them -- no threshold to tune and no
 * flicker at a seam. An element marked data-activate="visible" plays whenever
 * any part of it is on screen instead; that is for a lone banner, which is not
 * competing with neighbours and can be shorter than half the viewport, so the
 * centre test could never pass for it while the page sits at the top.
 *
 * The source is assigned here rather than in markup, so nothing is fetched until
 * an element becomes active. Shopify publishes only one progressive mp4 per
 * video -- the largest -- and keeps the lighter 720p and 480p renditions inside
 * the HLS ladder, so a browser that reports native HLS support gets the playlist,
 * but only on small screens. Shopify's adaptive ladder starts on the 480p rung
 * and only steps up to 1080p a few seconds in; that start-up dip is unnoticeable
 * on a phone-width frame and worth the bandwidth saving, but plainly visible on
 * a wide desktop hero, so desktop always takes the single largest mp4 instead.
 * Everything else gets the mp4, as does a browser that claims HLS support and
 * then fails to load it.
 */
(function () {
  const MOBILE_QUERY = '(max-width: 749px)';

  const stillPreferred = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const connection = navigator.connection;
  const dataSaver = Boolean(connection && connection.saveData);
  const disabled = stillPreferred || dataSaver;

  const onScreen = new Set();
  let observer = null;
  let queued = false;
  let listening = false;

  const evaluate = () => {
    queued = false;
    const centre = window.innerHeight / 2;
    onScreen.forEach((element) => {
      const box = element.getBoundingClientRect();
      const anywhereOnScreen = box.bottom > 0 && box.top < window.innerHeight;
      // Membership of onScreen already implies intersection, but the observer
      // reports asynchronously, so the box is re-tested rather than assumed.
      element.setActive(element.dataset.activate === 'visible' ? anywhereOnScreen : box.top <= centre && box.bottom > centre);
    });
  };

  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(evaluate);
  };

  const listen = () => {
    if (listening) return;
    listening = true;
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
  };

  const getObserver = () => {
    if (observer) return observer;
    // The observer only keeps the on-screen set small; the centre test below is
    // what decides. Without it every scroll frame would measure every stripe.
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          onScreen.add(entry.target);
        } else {
          onScreen.delete(entry.target);
          entry.target.setActive(false);
        }
      });
      queue();
    });
    return observer;
  };

  class AmbientVideo extends HTMLElement {
    connectedCallback() {
      if (disabled) return;
      this.media = this.querySelector('video');
      if (!this.media) return;
      this.active = false;
      this.sourced = false;
      this.awaitingLoad = false;
      this.usingHls = false;
      this.hlsFailed = false;
      getObserver().observe(this);
      listen();
      queue();
    }

    disconnectedCallback() {
      onScreen.delete(this);
      if (observer) observer.unobserve(this);
      if (this.media) this.media.pause();
    }

    setActive(next) {
      if (!this.media || next === this.active) return;
      this.active = next;
      if (next) this.start();
      else this.stop();
    }

    start() {
      if (!this.sourced) {
        // The first source assignment is held back until the page has finished
        // loading. The hero always contains the viewport centre at load, so an
        // unconditional assignment here would start a multi-MB mp4 fetch that
        // races the poster image -- which is meant to be the page's LCP element.
        if (document.readyState !== 'complete') {
          if (!this.awaitingLoad) {
            this.awaitingLoad = true;
            window.addEventListener(
              'load',
              () => {
                this.awaitingLoad = false;
                // Only resume if still active: this element may have gone out of
                // view (and been deactivated) while waiting for load to fire.
                if (this.active) this.start();
              },
              { once: true },
            );
          }
          return;
        }
        const source = this.pickSource();
        if (!source) return;
        this.media.addEventListener('playing', () => this.setAttribute('data-playing', ''), { once: true });
        this.media.src = source;
        this.sourced = true;
      }
      const started = this.media.play();
      // Autoplay can still be refused. Failing quietly leaves the poster up,
      // which is the correct fallback, so the rejection is deliberately swallowed.
      if (started && typeof started.catch === 'function') started.catch(() => {});
    }

    // The mp4 rendition for the current breakpoint. Also the fallback used
    // when a chosen HLS playlist fails to play.
    mp4Source() {
      const mobile = window.matchMedia(MOBILE_QUERY).matches;
      return (mobile && this.media.dataset.srcMobile) || this.media.dataset.srcDesktop;
    }

    pickSource() {
      const hls = this.media.dataset.srcHls;
      const smallScreen = window.matchMedia(MOBILE_QUERY).matches;
      // The standard native-HLS feature test: Safari on macOS and iOS, and
      // Chrome on Android, answer it with a non-empty string; desktop Chrome,
      // Firefox and Edge return ''. Those without native support fall back to
      // the progressive mp4, which is the only rendition Shopify guarantees.
      // Restricted to small screens -- see the file header for why.
      if (smallScreen && hls && this.media.canPlayType('application/vnd.apple.mpegurl')) {
        this.usingHls = true;
        this.media.addEventListener('error', () => this.fallbackToMp4(), { once: true });
        return hls;
      }
      return this.mp4Source();
    }

    // Runs at most once per element: guarded by hlsFailed, and the listener
    // that calls this was bound with { once: true } so it cannot itself fire
    // twice -- an error on the mp4 fallback does not loop back here.
    fallbackToMp4() {
      if (!this.usingHls || this.hlsFailed) return;
      this.hlsFailed = true;
      this.usingHls = false;
      const source = this.mp4Source();
      if (!source) return;
      this.media.src = source;
      const started = this.media.play();
      if (started && typeof started.catch === 'function') started.catch(() => {});
    }

    stop() {
      this.media.pause();
      this.media.currentTime = 0;
    }
  }

  if (!customElements.get('ambient-video')) {
    customElements.define('ambient-video', AmbientVideo);
  }
})();
